import type { PopconfirmProps } from 'antd';
import type React from 'react';
import type { ReactElement } from 'react';
import { cloneElement, isValidElement, useEffect, useRef } from 'react';
import { useDangerConfirm } from '../../hooks/useDangerConfirm';

export type ConfirmPopoverProps = PopconfirmProps;

// 全站删除/危险确认统一走 useDangerConfirm 弹窗。
// 保留原 Popconfirm 同款 API(children 作触发器 + title/description/onConfirm),
// 受控 open 与非受控两种用法都兼容,调用方零改动。
export default function ConfirmPopover({
  title,
  description,
  okText,
  cancelText,
  onConfirm,
  open,
  onOpenChange,
  children,
}: ConfirmPopoverProps) {
  const dangerConfirm = useDangerConfirm();
  const firedRef = useRef(false);

  const fire = () => {
    dangerConfirm({
      title: typeof title === 'function' ? title() : title,
      description:
        typeof description === 'function' ? description() : description,
      tone: 'danger',
      okText: typeof okText === 'string' ? okText : undefined,
      cancelText: typeof cancelText === 'string' ? cancelText : undefined,
      onOk: () => onConfirm?.(),
    });
  };

  // 受控:open 翻 true 时弹一次,并立即释放受控状态(弹窗自管生命周期)。
  // 依赖只取 open —— 其余刻意排除,避免重复弹窗。
  useEffect(() => {
    if (open && !firedRef.current) {
      firedRef.current = true;
      fire();
      onOpenChange?.(false);
    }
    if (!open) firedRef.current = false;
  }, [open]);

  // 非受控:点击 children 即弹窗。
  if (open === undefined && isValidElement(children)) {
    const child = children as ReactElement<{
      onClick?: (e: React.MouseEvent) => void;
    }>;
    const prevOnClick = child.props.onClick;
    return cloneElement(child, {
      onClick: (e: React.MouseEvent) => {
        prevOnClick?.(e);
        fire();
      },
    });
  }

  // 受控:children 原样渲染,其自身 action 负责把 open 置 true。
  return <>{children}</>;
}
